/**
 * Le haut de l'écran sous Safari d'iPhone et d'iPad.
 *
 * Depuis iOS 26, Safari dessine la page sous l'heure et la batterie et
 * brouille une bande qui déborde sous elles : l'en-tête de SwissTech, posé
 * juste en dessous, sortait flou. Sous Safari (pas dans l'application
 * installée, où la barre d'état reste nette), l'en-tête descend donc d'un
 * cran, et une bande fixe à sa couleur couvre le haut : Safari en reprend la
 * teinte au lieu de flouter la page.
 *
 * Dans l'application installée, la barre d'état est désormais pleine
 * (index.html, sans « black-translucent ») : la page commence sous l'heure et
 * rien ne se floute. Une icône ajoutée à l'écran d'accueil avant ce
 * changement garde l'ancienne barre transparente tant qu'elle n'est pas
 * ajoutée à nouveau : elle reçoit le même traitement que Safari.
 */

import { estInstallee } from '../pwa/installation';

/** Marge ajoutée sous la barre d'état de Safari, en pixels. */
export const MARGE_SAFARI = 28;

/**
 * @param {Navigator} [nav] navigateur
 * @param {() => boolean} [installee] vrai dans l'application installée
 * @returns {boolean} vrai sous Safari (ou un navigateur iOS) hors application installée
 */
export function iosDansLeNavigateur(nav = globalThis.navigator, installee = estInstallee) {
  if (!nav) return false;
  const ios =
    /iP(hone|ad|od)/.test(nav.userAgent || '') ||
    (nav.platform === 'MacIntel' && Number(nav.maxTouchPoints) > 1);
  return ios && nav.standalone !== true && !installee();
}

/**
 * Hauteur de la barre d'état sous laquelle la page passe, en pixels : zéro
 * quand la barre d'état est pleine.
 *
 * @param {Document} [doc] document
 * @returns {number} env(safe-area-inset-top) mesuré
 */
export function hauteurSousLaBarre(doc = globalThis.document) {
  const corps = doc?.body;
  if (!corps || typeof getComputedStyle !== 'function') return 0;
  const sonde = doc.createElement('div');
  sonde.style.cssText =
    'position:absolute;visibility:hidden;pointer-events:none;padding-top:env(safe-area-inset-top,0px)';
  corps.appendChild(sonde);
  const haut = parseFloat(getComputedStyle(sonde).paddingTop) || 0;
  sonde.remove();
  return haut;
}

/**
 * @param {Navigator} [nav] navigateur
 * @param {() => number} [sousLaBarre] hauteur de la page sous la barre d'état
 * @returns {boolean} vrai pour une icône d'iPhone ajoutée avant la barre
 *   d'état pleine : la page y passe encore sous l'heure
 */
export function ancienneIconeIos(nav = globalThis.navigator, sousLaBarre = hauteurSousLaBarre) {
  if (!nav || globalThis.Capacitor?.isNativePlatform?.()) return false;
  const ios =
    /iP(hone|ad|od)/.test(nav.userAgent || '') ||
    (nav.platform === 'MacIntel' && Number(nav.maxTouchPoints) > 1);
  return ios && nav.standalone === true && sousLaBarre() > 0;
}

/**
 * @returns {boolean} vrai quand le haut de la page passe sous une barre d'état
 *   que l'iPhone floute : Safari, ou une ancienne icône de l'écran d'accueil
 */
export function hautFloute() {
  return iosDansLeNavigateur() || ancienneIconeIos();
}

/**
 * Pose la marge du haut sur la page : `--st-haut` vaut la barre d'état, plus
 * la marge sous Safari d'iPhone.
 *
 * @param {Document} [doc] document
 */
export function poseLeBordDuHaut(doc = globalThis.document) {
  if (!doc?.documentElement) return;
  if (hautFloute()) {
    doc.documentElement.style.setProperty('--st-marge-haut', MARGE_SAFARI + 'px');
    doc.documentElement.dataset.iosNavigateur = '1';
  }
}

/**
 * Le fond de la page, sous l'heure et la batterie, à la couleur de l'en-tête.
 *
 * <p>Là où l'iPhone ou l'iPad floute le haut (Safari, ancienne icône), le flou
 * mêle à la bande de l'en-tête le fond du document, clair : le haut de l'écran
 * sortait voilé, gris-bleu. Le fond du document prend la couleur de l'en-tête :
 * le flou ne mêle plus que du bleu foncé, et l'heure se lit nette. Le corps de
 * la page garde son fond clair.
 *
 * @param {string} couleur couleur de l'en-tête
 * @param {Document} [doc] document
 * @returns {(() => void)|undefined} remet le fond d'avant ; rien hors iOS floué
 */
export function poseLeFondDuHaut(couleur, doc = globalThis.document) {
  const racine = doc?.documentElement;
  if (!racine || !hautFloute()) return undefined;
  const avant = racine.style.backgroundColor;
  racine.style.backgroundColor = couleur;
  return () => {
    racine.style.backgroundColor = avant;
  };
}
